import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, of, map } from 'rxjs';
import { utils } from '../helper/utils';
import { ContainerGradeRepairCodeHeader } from '../models/container-grade-repair-code.model';
import { ContainerGradeRepairCodeDetailView } from '../models/container-grade-repair-code-detail-view.model';
import {
  ContainerGradeRepairCodeFilter,
  ContainerGradeRepairCodeFilterFormValue,
} from '../models/container-grade-repair-code-filter.model';
import { CreateContainerGradeRepairCodeRequest } from '../models/create-container-grade-repair-code-request.model';
import { AuditLog } from '../models/audit-log.model';
import { DownloadRequest } from '../models/download-filter.model';

export interface RepairCodeOption {
  key: string;
  value: string;
}

type UpdateContainerGradeRepairCodeRequest = {
  generateGradeChange: boolean;
  gradesApplicable: string[];
};

@Injectable({
  providedIn: 'root',
})
export class ContainerGradeRepairCodeService {
  constructor(private readonly httpClient: HttpClient) {}

  private filterFormValue: ContainerGradeRepairCodeFilterFormValue | null = null;

  setFilterFormValue(value: ContainerGradeRepairCodeFilterFormValue | null): void {
    this.filterFormValue = value;
  }

  getFilterFormValue(): ContainerGradeRepairCodeFilterFormValue | null {
    return this.filterFormValue;
  }

  private buildApiParams(
    page: number,
    pageLimit: number,
    sort?: string,
    filters?: ContainerGradeRepairCodeFilter,
  ): { [param: string]: string | number | boolean } {
    const params: { [param: string]: string | number | boolean } = {};
    params['Page'] = page;
    params['Limit'] = pageLimit > 0 ? pageLimit : 100;
    if (sort) {
      params['sort'] = sort;
    }
    if (!filters) {
      return params;
    }
    if (filters.RepairCodeList && filters.RepairCodeList.length > 0) {
      params['RepairCodeList'] = filters.RepairCodeList.join(',');
    }
    if (filters.RepairModeList && filters.RepairModeList.length > 0) {
      params['RepairModeList'] = filters.RepairModeList.join(',');
    }
    if (filters.GradesApplicableList && filters.GradesApplicableList.length > 0) {
      params['GradesApplicableList'] = filters.GradesApplicableList.join(',');
    }
    if (filters.GenerateGradeChange !== undefined && filters.GenerateGradeChange !== null) {
      params['GenerateGradeChange'] = filters.GenerateGradeChange;
    }
    if (filters.SearchText) {
      params['SearchText'] = filters.SearchText;
    }
    return params;
  }

  getList(
    page: number,
    pageLimit: number,
    sort?: string,
    filters?: ContainerGradeRepairCodeFilter,
  ): Observable<HttpResponse<ContainerGradeRepairCodeHeader[]>> {
    const params = this.buildApiParams(page, pageLimit, sort, filters);
    const url = utils.GetMasterDataManagementUrl('container-grade-repair-code');
    return this.httpClient.get<ContainerGradeRepairCodeHeader[]>(url, { params, observe: 'response' }).pipe(
      map((response: HttpResponse<ContainerGradeRepairCodeHeader[]>) => {
        const body = response.body;
        if (body) {
          const mappedBody = body.map((item) => ({
            ...item,
            id: `${item.repairCode}_${item.mode}`,
          }));
          return new HttpResponse({ body: mappedBody, headers: response.headers });
        }
        return response;
      }),
      catchError(() => of(new HttpResponse<ContainerGradeRepairCodeHeader[]>({ body: [], headers: undefined }))),
    );
  }

  getByRepairCodeAndMode(repairCode: string, repairMode: string): Observable<ContainerGradeRepairCodeDetailView | null> {
    const path = `container-grade-repair-code/${encodeURIComponent(repairCode)}/${encodeURIComponent(repairMode)}`;
    const url = utils.GetMasterDataManagementUrl(path);
    return this.httpClient.get<ContainerGradeRepairCodeDetailView>(url).pipe(catchError(() => of(null)));
  }

  create(request: CreateContainerGradeRepairCodeRequest): Observable<ContainerGradeRepairCodeDetailView> {
    const url = utils.GetMasterDataManagementUrl('container-grade-repair-code');
    return this.httpClient.post<ContainerGradeRepairCodeDetailView>(url, request);
  }

  update(
    repairCode: string,
    repairMode: string,
    request: UpdateContainerGradeRepairCodeRequest,
  ): Observable<ContainerGradeRepairCodeDetailView> {
    const path = `container-grade-repair-code/${encodeURIComponent(repairCode)}/${encodeURIComponent(repairMode)}`;
    const url = utils.GetMasterDataManagementUrl(path);
    return this.httpClient.put<ContainerGradeRepairCodeDetailView>(url, request);
  }

  delete(repairCode: string, repairMode: string): Observable<void> {
    const path = `container-grade-repair-code/${encodeURIComponent(repairCode)}/${encodeURIComponent(repairMode)}`;
    const url = utils.GetMasterDataManagementUrl(path);
    return this.httpClient.delete<void>(url);
  }

  getAllCodes(): Observable<RepairCodeOption[]> {
    const url = utils.GetMasterDataManagementUrl('container-grade-repair-code/all/codes');
    return this.httpClient.get<RepairCodeOption[]>(url).pipe(catchError(() => of([])));
  }

  getAllModes(): Observable<string[]> {
    const url = utils.GetMasterDataManagementUrl('container-grade-repair-code/all/modes');
    return this.httpClient.get<string[]>(url).pipe(catchError(() => of([])));
  }

  getAuditLogs(
    repairCode: string,
    repairMode: string,
    isSystemLog: boolean = false,
    page: number = 1,
    limit: number = 100,
  ): Observable<HttpResponse<AuditLog[]>> {
    const path = `container-grade-repair-code/${encodeURIComponent(repairCode)}/${encodeURIComponent(repairMode)}/audit-logs?isSystemLog=${isSystemLog}&page=${page}&limit=${limit}`;
    return this.httpClient
      .get<AuditLog[]>(utils.GetMasterDataManagementUrl(path), { observe: 'response' })
      .pipe(catchError(() => of(new HttpResponse<AuditLog[]>({ body: [], status: 200, statusText: 'OK' }))));
  }

  downloadToExcel(request: DownloadRequest): Observable<Blob> {
    const url = utils.GetDownloadUrl('repair-code-grade-mapping');
    return this.httpClient.post(url, request, { responseType: 'blob' });
  }
}
